import { useEffect, useRef } from "react";
import Quill from "quill";
import DOMPurify from "dompurify";
import "quill/dist/quill.snow.css";

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  className,
  style,
}) {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);

  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    const container = containerRef.current;
    // Quill inserts its toolbar beside the editor. Keep both inside a wrapper
    // owned by this effect so StrictMode cleanup removes the whole instance.
    const editor = container.appendChild(document.createElement("div"));
    const quill = new Quill(editor, {
      theme: "snow",
    });
    quillRef.current = quill;

    // SECURITY: setting innerHTML directly bypasses Quill's sanitizer; sanitize
    // first so existing/loaded content can't carry stored XSS into the editor.
    if (valueRef.current)
      quill.root.innerHTML = DOMPurify.sanitize(valueRef.current);

    const handleTextChange = () => {
      const html = quill.root.innerHTML;
      if (html !== valueRef.current) {
        valueRef.current = html;
        onChangeRef.current(html);
      }
    };
    quill.on("text-change", handleTextChange);

    return () => {
      quill.off("text-change", handleTextChange);
      quill.disable();
      quillRef.current = null;
      container.replaceChildren();
    };
  }, []);

  useEffect(() => {
    quillRef.current.root.setAttribute("data-placeholder", placeholder ?? "");
  }, [placeholder]);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill || value === valueRef.current) return;
    valueRef.current = value;
    quill.root.innerHTML = DOMPurify.sanitize(value ?? "");
  }, [value]);

  return <div ref={containerRef} className={className} style={style} />;
}
